import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../Images/Qualified-Stucco-logo.png";
import "../Styles/Admin.css";

function AdminDashboard() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, replied: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");
  const adminName = localStorage.getItem("adminName") || "Admin";

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchContacts();
  }, [token, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContacts = async () => {
    try {
      const [contactsRes, statsRes] = await Promise.all([
        fetch("http://localhost:5000/api/contacts", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("http://localhost:5000/api/contacts/stats", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!contactsRes.ok) {
        localStorage.removeItem("adminToken");
        navigate("/admin");
        return;
      }

      setContacts(await contactsRes.json());
      setStats(await statsRes.json());
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    await fetch(`http://localhost:5000/api/contacts/${id}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });
    fetchContacts();
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    await fetch(`http://localhost:5000/api/contacts/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setSelectedContact(null);
    fetchContacts();
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminName");
    navigate("/admin");
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const classes = {
      new: "badge-new",
      read: "badge-read",
      replied: "badge-replied",
    };
    return <span className={`admin-badge ${classes[status]}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <img src={Logo} alt="Qualified Stucco" className="admin-sidebar-logo-img" />
        </div>
        <nav className="admin-nav">
          <a href="#!" className="admin-nav-item active">
            <span>📬</span> Messages
          </a>
        </nav>
        <div className="admin-sidebar-footer">
          <div className="admin-user">
            <div className="admin-user-avatar">{adminName.charAt(0)}</div>
            <span>{adminName}</span>
          </div>
          <button onClick={logout} className="admin-logout-btn">Logout</button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-topbar">
          <h1>Dashboard</h1>
          <p className="admin-date">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
        </div>

        {/* Stats */}
        <div className="admin-stats">
          <div className="admin-stat-card">
            <div className="admin-stat-icon admin-stat-total">📩</div>
            <div><p className="admin-stat-number">{stats.total}</p><p className="admin-stat-label">Total Messages</p></div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon admin-stat-new">🆕</div>
            <div><p className="admin-stat-number">{stats.new}</p><p className="admin-stat-label">New</p></div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon admin-stat-read">👁️</div>
            <div><p className="admin-stat-number">{stats.read}</p><p className="admin-stat-label">Read</p></div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon admin-stat-replied">✅</div>
            <div><p className="admin-stat-number">{stats.replied}</p><p className="admin-stat-label">Replied</p></div>
          </div>
        </div>

        {/* Messages Table */}
        <div className="admin-table-container">
          <div className="admin-table-header">
            <h2>Contact Messages</h2>
            <span className="admin-table-count">{contacts.length} messages</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c._id} className={c.status === "new" ? "row-new" : ""}>
                    <td className="td-name">{c.name}</td>
                    <td>{c.email}</td>
                    <td>{c.service || "—"}</td>
                    <td>{getStatusBadge(c.status)}</td>
                    <td className="td-date">{formatDate(c.createdAt)}</td>
                    <td>
                      <button className="admin-view-btn" onClick={() => setSelectedContact(c)}>View</button>
                    </td>
                  </tr>
                ))}
                {contacts.length === 0 && (
                  <tr><td colSpan="6" className="td-empty">No messages yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Message Detail Modal */}
      {selectedContact && (
        <div className="admin-modal-overlay" onClick={() => setSelectedContact(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Message Details</h2>
              <button onClick={() => setSelectedContact(null)} className="admin-modal-close">×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-field">
                <label>Name</label><p>{selectedContact.name}</p>
              </div>
              <div className="admin-modal-field">
                <label>Email</label><p>{selectedContact.email}</p>
              </div>
              <div className="admin-modal-field">
                <label>Phone</label><p>{selectedContact.phone || "Not provided"}</p>
              </div>
              <div className="admin-modal-field">
                <label>Service</label><p>{selectedContact.service || "Not specified"}</p>
              </div>
              <div className="admin-modal-field">
                <label>Message</label><p className="admin-modal-message">{selectedContact.message}</p>
              </div>
              <div className="admin-modal-field">
                <label>Received</label><p>{formatDate(selectedContact.createdAt)}</p>
              </div>
              <div className="admin-modal-field">
                <label>Status</label>{getStatusBadge(selectedContact.status)}
              </div>
            </div>
            <div className="admin-modal-actions">
              <select
                value={selectedContact.status}
                onChange={(e) => { updateStatus(selectedContact._id, e.target.value); setSelectedContact({ ...selectedContact, status: e.target.value }); }}
                className="admin-status-select"
              >
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="replied">Replied</option>
              </select>
              <button onClick={() => deleteContact(selectedContact._id)} className="admin-delete-btn">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
